import { useQuery } from '@tanstack/react-query';

import { companyApi } from '@/api';
import {
  BoxAvatar,
  BoxList,
  BoxRow,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate } from '@/utils/format';

/** More than any company keeps live at once; the list is not paged. */
const LIVE_LIMIT = 100;

const HOW_IT_WORKS = [
  {
    icon: 'zap',
    title: 'Skill match',
    body: 'Each candidate is scored against the skills a listing asks for, and the score says which ones they have and which they are missing.',
  },
  {
    icon: 'sort',
    title: 'Ranked applicants',
    body: 'Applicants can be ordered by that score instead of by date, so the strongest fits are read first.',
  },
  {
    icon: 'userSearch',
    title: 'Recommended candidates',
    body: 'Students who have not applied yet but fit the listing, and are open to offers. A paid-plan feature.',
  },
];

/**
 * Matching for every live listing in one place: who fits each one, and its
 * applicants in order of fit. The pages it links to do the work; this is the
 * way in, so the sidebar has somewhere to send "AI features".
 */
export function CompanyRecommendationsPage() {
  const { can } = usePermissions();
  const { needsVerification } = useCompanyVerification();
  const canSource = can(PERMISSIONS.CANDIDATE_READ);
  const canReadApplications = can(PERMISSIONS.APPLICATION_READ);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-internships', 'PUBLISHED', 'recommendations'],
    queryFn: () =>
      companyApi.listInternships({ page: 1, page_size: LIVE_LIMIT, statuses: 'PUBLISHED' }),
  });

  const listings = data?.data ?? [];

  return (
    <div>
      <PageHeader
        eyebrow="AI features"
        title="AI recommendations"
        description="Candidates matched to each live internship, and its applicants ranked by how well they fit."
      />

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          {isPending ? (
            <LoadingState label="Loading your live internships…" />
          ) : isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : listings.length === 0 ? (
            <EmptyState
              icon={<Icon name="sparkles" size={22} />}
              title="No live internships yet"
              description="Recommendations are made for published listings. Publish one and its matches appear here."
              action={
                can(PERMISSIONS.INTERNSHIP_CREATE) &&
                (needsVerification ? (
                  <VerifyToContinueButton />
                ) : (
                  <LinkButton to={paths.company.internshipNew}>Post an internship</LinkButton>
                ))
              }
            />
          ) : (
            <BoxList>
              {listings.map((row) => (
                <BoxRow
                  key={row.id}
                  accent="success"
                  leading={<BoxAvatar icon="internship" />}
                  title={row.title}
                  facts={[
                    { icon: 'home', text: row.location ?? 'No location' },
                    {
                      icon: 'calendar',
                      text: row.application_deadline
                        ? `Deadline ${formatDate(row.application_deadline)}`
                        : 'No deadline',
                    },
                  ]}
                  actions={
                    <>
                      {canReadApplications && (
                        <LinkButton
                          size="sm"
                          variant="secondary"
                          to={`${paths.company.applications}?sort=match&internship=${row.id}`}
                        >
                          <Icon name="sort" size={15} />
                          Applicants by fit
                        </LinkButton>
                      )}
                      {canSource && (
                        <LinkButton size="sm" to={paths.company.internshipSourcing(row.id)}>
                          <Icon name="sparkles" size={15} />
                          Recommended candidates
                        </LinkButton>
                      )}
                    </>
                  }
                />
              ))}
            </BoxList>
          )}
        </div>

        <Card>
          <CardHeader title="How matching works" />
          <ul className="space-y-4">
            {HOW_IT_WORKS.map((item) => (
              <li key={item.title} className="flex items-start gap-3 text-sm">
                <span
                  aria-hidden="true"
                  className="bg-brand-50 text-brand-700 grid size-8 shrink-0 place-items-center rounded-lg"
                >
                  <Icon name={item.icon} size={15} />
                </span>
                <span>
                  <span className="text-ink block font-medium">{item.title}</span>
                  <span className="text-ink-muted">{item.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
